import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/about.css';
import Logo from '../assets/logo.png';
import FukudaToshio from '../assets/福田敏男.png';
import KubotaNaoyuki from '../assets/久保田直行.png';
import XuChunshan from '../assets/许春山.png';
import manabe from '../assets/真鍋智章.png';
import yoshida from '../assets/吉田楓.png';
import matsumoto from '../assets/松本有.png';
import tanaka from '../assets/田中理.png';
import igarashi from '../assets/五十嵐章夫.png';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';

// 后续如需展示社长照片，请将图片放到 src/assets/president-message.jpg 后取消下一行注释
// import PresidentImage from '../assets/president-message.jpg';

const About: React.FC = () => {
    const { t } = useTranslation();
    const pageRef = useRef<HTMLDivElement>(null);
    const avatarMap: Record<string, string> = {
        fukuda: FukudaToshio,
        kubota: KubotaNaoyuki,
        xu: XuChunshan,
        manabe: manabe,
        yoshida: yoshida,
        matsumoto: matsumoto,
        tanaka: tanaka,
        igarashi: igarashi,
    };

    useRevealAnimation(pageRef);
    useDocumentTitle(t('About.hero.title'));

    const businessItems = t('About.business.items', { returnObjects: true }) as string[];
    const teamMembers = t('Home.about.teamMembers', { returnObjects: true }) as Array<{ name: string; imgKey: string }>;
    const solveItems = t('About.philosophy.solveItems', { returnObjects: true }) as Array<{
        key: string;
        title: string;
        desc: string;
    }>;
    const missionItems = t('About.mission.items', { returnObjects: true }) as string[];
    const values = t('About.values.items', { returnObjects: true }) as string[];
    const marketStats = t('About.strengths.stats', { returnObjects: true }) as Array<{ value: string; label: string }>;

    return (
        <div className="about-page" ref={pageRef}>
            <section className="about-hero-section">
                <div className="about-hero-background">
                    <div className="about-grid-lines"></div>
                    <div className="about-radial-glow"></div>
                </div>
                <div className="section-container about-hero-layout">
                    <div className="about-hero-copy reveal">
                        <span className="about-kicker">{t('About.hero.kicker')}</span>
                        <div className="about-hero-brand">
                            <img src={Logo} alt="Future World Logo" className="about-page-logo" />
                            <div>
                                <h1 className="about-hero-title">{t('About.hero.title')}</h1>
                                <p className="about-hero-subtitle">{t('About.hero.subtitle')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section-container about-content-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('About.company.subtitle')}</span>
                    <h2 className="section-title">{t('About.company.title')}</h2>
                </div>
                <div className="about-company-panel reveal delay-1">
                    <div className="about-company-intro">
                        <div className="about-company-heading">
                            <span className="about-mini-label">{t('About.company.cardTitle')}</span>
                            <h3>{t('About.hero.title')}</h3>
                            <p>{t('About.hero.subtitle')}</p>
                        </div>
                        <div className="about-company-description">
                            <p>{t('About.company.description')}</p>
                        </div>
                    </div>
                    <div className="about-company-business">
                        <span className="about-mini-label">{t('About.business.label')}</span>
                        <div className="about-business-list">
                            {businessItems.map((item, index) => (
                                <div className="about-business-item" key={index}>
                                    <span className="about-business-index">{String(index + 1).padStart(2, '0')}</span>
                                    <p>{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="section-container about-content-section" id="core-people">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('About.strengths.sectionLabel')}</span>
                    <h2 className="section-title">{t('About.strengths.sectionTitle')}</h2>
                </div>
                <div className="about-strengths-layout">
                    <div className="about-strengths-main reveal delay-1">
                        <span className="about-mini-label">{t('About.strengths.marketLabel')}</span>
                        <h3>{t('About.strengths.marketTitle')}</h3>
                        <div className="about-strengths-stats">
                            {marketStats.map((item) => (
                                <article className="about-stat-card" key={item.label}>
                                    <strong>{item.value}</strong>
                                    <span>{item.label}</span>
                                </article>
                            ))}
                        </div>
                    </div>
                    <Link to="/resources/core-people" className="about-team-panel reveal delay-2">
                        <div className="about-team-panel-header">
                            <span className="about-mini-label">CORE TEAM</span>
                            <h3>{t('Home.about.teamTitle')}</h3>
                            <span className="about-team-cta">{t('Home.about.teamCta')}</span>
                        </div>
                        <div className="about-team-grid">
                            {teamMembers.map((member, index) => (
                                <article className="about-team-member" key={`${member.imgKey}-${index}`}>
                                    <div className="about-team-avatar">
                                        {avatarMap[member.imgKey] ? (
                                            <img src={avatarMap[member.imgKey]} alt={member.name} className="about-team-avatar-image" />
                                        ) : (
                                            <span>{member.name.slice(0, 1)}</span>
                                        )}
                                    </div>
                                    {/*
                                    后续如需替换为真实人物照片，可将上方色块占位替换为图片元素，例如：
                                    <img src={MemberPhoto} alt={member.name} className="about-team-avatar-image" />
                                    */}
                                    <div className="about-team-name">{member.name}</div>
                                </article>
                            ))}
                        </div>
                    </Link>
                </div>
            </section>

            <section className="section-container about-content-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('About.philosophy.label')}</span>
                    <h2 className="section-title">{t('About.philosophy.title')}</h2>
                </div>
                <div className="about-philosophy-layout">
                    <div className="about-philosophy-main reveal delay-1">
                        <span className="about-mini-label">{t('About.philosophy.label')}</span>
                        <div className="about-philosophy-headline">{t('About.philosophy.headline')}</div>
                        <div className="about-philosophy-formula">{t('About.philosophy.formula')}</div>
                        <p className="about-philosophy-intro">{t('About.philosophy.description')}</p>
                    </div>
                    <div className="about-solve-rail">
                        {solveItems.map((item, index) => (
                            <article className="about-solve-card reveal" key={item.key} style={{ transitionDelay: `${0.1 * (index + 1)}s` }}>
                                <span className="about-solve-key">{item.key}</span>
                                <div className="about-solve-content">
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-container about-content-section">
                <div className="about-mvv-grid">
                    <div className="about-panel reveal">
                        <span className="about-mini-label">{t('About.mission.label')}</span>
                        <h2>{t('About.mission.title')}</h2>
                        <p className="about-panel-quote">{t('About.mission.quote')}</p>
                        <div className="about-bullet-list">
                            {missionItems.map((item, index) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>
                    </div>
                    <div className="about-side-stack">
                        <div className="about-panel reveal delay-1">
                            <span className="about-mini-label">{t('About.vision.label')}</span>
                            <h2>{t('About.vision.title')}</h2>
                            <p className="about-panel-quote">{t('About.vision.quote')}</p>
                        </div>
                        <div className="about-panel reveal delay-2">
                            <span className="about-mini-label">{t('About.values.label')}</span>
                            <h2>{t('About.values.title')}</h2>
                            <div className="about-values-list">
                                {values.map((value, index) => (
                                    <span className="about-value-pill" key={index}>{value}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section-container about-content-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('About.greeting.label')}</span>
                    <h2 className="section-title">{t('About.greeting.title')}</h2>
                </div>
                <div className="about-greeting-layout">
                    <div className="about-greeting-visual reveal delay-1">
                        <div className="about-image-placeholder president-image-placeholder">
                            <span>{t('About.placeholders.president')}</span>
                        </div>

                        {/*
                        将上方灰色色块替换为真实图片时，删除上面的占位块并取消下面这段注释即可
                        <img
                            src={PresidentImage}
                            alt={t('About.greeting.title')}
                            className="about-real-image president-real-image"
                        />
                        */}
                    </div>
                    <div className="about-greeting-card reveal delay-2">
                        <p>{t('About.greeting.paragraph1')}</p>
                        <p>{t('About.greeting.paragraph2')}</p>
                        <p>{t('About.greeting.paragraph3')}</p>
                    </div>
                </div>
            </section>

            <section className="section-container about-content-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('About.contact.label')}</span>
                    <h2 className="section-title">{t('About.contact.title')}</h2>
                </div>
                <div className="about-contact-card reveal delay-1">
                    <div className="about-contact-grid">
                        <div className="about-contact-item">
                            <span className="about-mini-label">{t('About.contact.officeLabel')}</span>
                            <p>{t('About.contact.officeAddress')}</p>
                        </div>
                        <div className="about-contact-item">
                            <span className="about-mini-label">{t('About.contact.phoneLabel')}</span>
                            <p>{t('About.contact.phone')}</p>
                        </div>
                        <div className="about-contact-item">
                            <span className="about-mini-label">{t('About.contact.emailLabel')}</span>
                            <p>{t('About.contact.email')}</p>
                        </div>
                        <div className="about-contact-item">
                            <span className="about-mini-label">{t('About.contact.personLabel')}</span>
                            <p>{t('About.contact.person')}</p>
                        </div>
                    </div>
                    <div className="about-contact-copyright">{t('About.contact.copyright')}</div>
                </div>
            </section>
        </div>
    );
};

export default About;

